import {
  Report,
  ReportType,
} from '@/shared/api/protocol-ts/model/dto_report_pb';
import { Report_CBCT_IOS_Superimposition_Orientation } from '@/shared/api/protocol-ts/model/dto_report_type_cbct_ios_superimposition_pb';

import { GroupedReportsWithStudies } from '../../../pages/PatientProfile/config';
import { StructuredReportsAndStudiesType } from '../config';

import { getLatestDate } from './getLatestDate';
import { sortReportByDate } from './sortReportByDate';

const getSuperimpositionOrientation = (report: Report) =>
  report.Specific.case === 'DataCBCTIOSSuperimposition'
    ? report.Specific.value.Orientation
    : Report_CBCT_IOS_Superimposition_Orientation.CBCT_IOS_Superimposition_Orientation_CBCT;

export const getStructuredReportsAndStudies = (
  groupedReportsWithStudies: GroupedReportsWithStudies[],
): StructuredReportsAndStudiesType[] =>
  groupedReportsWithStudies.flatMap(
    (group): StructuredReportsAndStudiesType[] => {
      const baseID = group.sourceStudyIDs.join('_');

      const buildReportEntry = (
        id: string,
        reportType: ReportType,
        reports: Report[],
      ): StructuredReportsAndStudiesType => {
        const latestReport =
          reports.length > 0
            ? reports.reduce((latest, current) =>
                sortReportByDate(latest, current) <= 0 ? latest : current,
              )
            : undefined;

        return {
          ID: id,
          activeReportIDs: reports?.map((report) => report.ID) || [],
          reportType,
          report: latestReport,
          studies: group.studies,
          date: getLatestDate(latestReport, group.studies),
        };
      };

      return Object.entries(group.reportsByType).flatMap(
        ([reportTypeKey, reports]) => {
          const reportType = Number(reportTypeKey) as ReportType;

          // Superimposition reports ordered on the same studies but with
          // different orientations must not collapse into one card — split
          // them so each orientation gets its own card.
          if (reportType !== ReportType.ReportType_CBCT_IOS_Superimposition) {
            return [
              buildReportEntry(`${baseID}_${reportType}`, reportType, reports),
            ];
          }

          const reportsByOrientation = reports.reduce<Record<number, Report[]>>(
            (accumulator, report) => {
              const orientation = getSuperimpositionOrientation(report);

              return {
                ...accumulator,
                [orientation]: [...(accumulator[orientation] || []), report],
              };
            },
            {},
          );

          return Object.entries(reportsByOrientation).map(
            ([orientation, orientationReports]) =>
              buildReportEntry(
                `${baseID}_${reportType}_${orientation}`,
                reportType,
                orientationReports,
              ),
          );
        },
      );
    },
  );
